<template>
  <div
    class="ds-config-demo"
    :class="{
      'ds-config-example-only': exampleIds && exampleIds.length > 0,
      'full-screen-throbber': appThrobber
    }"
  >
    <cat-throbber loading-message="Generating examples..." />
    <div v-if="configError">Examples not available</div>
    <template v-for="example in filteredExamples" :key="example.name">
      <div class="demo-container">
        <div v-if="!exampleIds" class="demo-example-description">
          <h3>{{ example.title }}</h3>
          <div
            :id="'desc_' + example.idx"
            class="demo-description"
            v-html="markdown(example.description)"
          ></div>
          <cat-expando-box
            :id="example.id"
            :label="configLabel(example.id)"
            @ui-click="toggleConfig($event, example.id)"
          >
            <div v-if="displayOnly !== true" class="demo-playable">
              <div v-if="!editing(example.name)">
                <div
                  class="ds-demo-code-block"
                  v-html="codeBlock(example.form, 'xml')"
                ></div>
                <div class="stack-horizontally left">
                  <button
                    v-if="noEdit !== true"
                    class="cat-btn secondary small mbs"
                    @click="edit(example.name)"
                  >
                    Edit XML
                  </button>
                </div>
              </div>
              <div v-else class="demo-xml-form">
                <textarea
                  :id="'form_' + example.idx"
                  v-model="example.form"
                  :style="textAreaStyle"
                />
                <button
                  class="cat-btn primary small"
                  @click="gen($event, example.idx)"
                >
                  Regen
                </button>
                <button class="cat-btn secondary small" @click="reset">
                  Reset
                </button>
                <button
                  class="cat-btn secondary small"
                  @click="closeEdit(example.name)"
                >
                  Hide editor
                </button>
              </div>
              <button
                v-show="!renderStateVisible(example.name)"
                class="cat-btn small secondary right"
                @click="toggleRenderState(example.name)"
              >
                Show render state
              </button>
              <div
                v-show="renderStateVisible(example.name)"
                class="ds-demo-render-state-code"
              >
                <button
                  class="cat-btn small secondary right mbt"
                  @click="toggleRenderState(example.name)"
                >
                  Hide render state
                </button>
                <textarea
                  :value="JSON.stringify(example.renderState, null, 2)"
                  :style="textAreaStyle"
                  @change="update($event)"
                />
              </div>
            </div>
            <div
              v-if="displayOnly !== true && noEdit !== true"
              class="pn"
              style="clear: both"
            >
              <a
                v-if="ruleReferences && ruleReferences.length"
                class="button small simple mln mrt"
                @click="toggleRules"
                >Toggle Rules</a
              >
              <div v-if="rulesOpen">
                <h4>Rule References</h4>
                <template v-for="(ref, key) in ruleReferences" :key="key">
                  <div>
                    <h4>{{ ref.name }}</h4>
                    <div
                      v-if="ref.documentation"
                      v-html="markdown(ref.documentation)"
                    ></div>
                    <template v-for="(rule, idx) in ref.rules" :key="idx + 1">
                      <div>
                        type: {{ rule.type }}
                        <div v-html="codeBlock(rule.rule, rule.type)"></div>
                      </div>
                    </template>
                  </div>
                </template>
              </div>
              <a
                v-if="dataConstraints && dataConstraints.length"
                class="button small simple mln mrt"
                @click="toggleDataConstraints"
                >Toggle Data Constraints</a
              >
              <div v-if="dataConstraintsOpen">
                <h4>Data Constraints</h4>
                <template v-for="(dc, key) in dataConstraints" :key="key">
                  <div>
                    Name: {{ dc.name }}, Type: {{ dc.type }}, Restrict:
                    {{ dc.restrict }}
                    <table class="table plain compact">
                      <tr>
                        <th>Code</th>
                        <th>Text Key</th>
                      </tr>
                      <template v-for="(val, idx) in dc.values" :key="idx + 1">
                        <tr>
                          <td>{{ idx }}</td>
                          <td>{{ val }}</td>
                        </tr>
                      </template>
                    </table>
                  </div>
                </template>
              </div>
            </div>
          </cat-expando-box>
        </div>
        <div class="demo-api-provider">
          <api-provider
            :id="example.id"
            :app-code="name"
            @emit-state="updateExample($event, example.idx)"
            @pre-flight="updateTextKeys($event, example.idx)"
          >
            <ex :display="display" :text="texts">
              <viewtree :viewtree="example.renderState" />
            </ex>
          </api-provider>
        </div>
      </div>
    </template>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import { ViewTree } from "@/design-system/widget-resources/types";
import Ex from "./ex.vue";
import ApiProvider from "@/components/api-provider.vue";
import CatExpandoBox from "@/components/catBox/catExpandoBox.vue";
import CatThrobber from "@/components/catPageTemplate/throbber.vue";

interface DemoExample {
  idx: number;
  name: string;
  form: string;
  renderState: ViewTree;
  description: string;
  title: string;
  id: string;
  sort: number;
}

interface TextConfig {
  [key: string]: string;
}

interface DataConstraintValues {
  [key: string]: string;
}

interface DataConstraintConfig {
  name: string;
  type: string;
  restrict: boolean;
  values: DataConstraintValues;
}

interface RuleReference {
  name: string;
  documentation: string;
  rules: Rule[];
}

interface Rule {
  type: string;
  rule: string;
}

@Component({
  name: "config-demo",
  components: { CatExpandoBox, Ex, ApiProvider, CatThrobber }
})
export default class ConfigDemo extends Vue {
  @Prop()
  public readonly name!: string;
  @Prop()
  public readonly displayOnly!: boolean;
  @Prop()
  public readonly exampleIds!: []; //render only the selected examples - no edit - read only
  @Prop()
  public readonly noEdit!: boolean;
  @Prop()
  public readonly unresolved!: boolean;
  @Prop()
  public readonly appThrobber!: boolean; // If true, renders the throbber sticky, full screen.

  public examples: DemoExample[] = [];
  public filteredExamples: DemoExample[] = [];
  public texts: TextConfig | null = null;
  public dataConstraints: DataConstraintConfig[] | null = null;
  public ruleReferences: RuleReference[] | null = null;

  public textsOpen: boolean = false;
  public rulesOpen: boolean = false;
  public expandedConfigs: string[] = [];
  public dataConstraintsOpen: boolean = false;
  public visibleRenderState: string[] = [];
  public editingForms: string[] = [];

  configError: boolean = false;

  @Prop()
  public readonly display!: any;

  update(event: any, index: number) {
    this.examples[index].renderState = JSON.parse(event.target.value);
  }

  toggleText() {
    this.textsOpen = !this.textsOpen;
  }

  toggleRules() {
    this.rulesOpen = !this.rulesOpen;
  }

  toggleDataConstraints() {
    this.dataConstraintsOpen = !this.dataConstraintsOpen;
  }

  toggleRenderState(name: string): void {
    if (!this.renderStateVisible(name)) {
      this.visibleRenderState.push(name);
    } else {
      this.visibleRenderState = this.visibleRenderState.filter(
        (e) => e !== name
      );
    }
  }

  toggleConfig(event: any, id: string) {
    if (event.value) {
      // Expand
      this.expandedConfigs.push(id);
    } else {
      // Collapse
      var index = this.expandedConfigs.indexOf(id);
      if (index >= 0) {
        this.expandedConfigs.splice(index, 1);
      }
    }
  }

  configLabel(id: string) {
    if (this.expandedConfigs.indexOf(id) >= 0) {
      // Currently expanded
      return "Hide configuration";
    }
    return "Show configuration";
  }

  edit(name: string): void {
    this.editingForms.push(name);
  }

  closeEdit(name: string): void {
    this.editingForms.splice(this.editingForms.indexOf(name), 1);
  }

  editing(name: string): boolean {
    return this.editingForms.indexOf(name) > -1;
  }

  renderStateVisible(name: string): boolean {
    return this.visibleRenderState.indexOf(name) > -1;
  }

  changeKey(event: Event, originalKey: string) {
    if (this.texts != null) {
      const txts: HTMLInputElement = event.target as HTMLInputElement;
      this.texts[txts.value] = this.texts[originalKey];
      delete this.texts[originalKey];
    }
  }

  changeText(event: Event, key: string) {
    if (this.texts != null) {
      const txts: HTMLInputElement = event.target as HTMLInputElement;
      this.texts[key] = txts.value;
    }
  }

  freezeNodeState(ns: any) {
    if (!ns || typeof ns !== "object") {
      return ns;
    }
    if (ns.kv) {
      ns.kv = Object.freeze(ns.kv);
    }
    if (ns.text) {
      ns.text = Object.freeze(ns.text);
    }
    if (ns.errors) {
      ns.errors = Object.freeze(ns.errors);
    }

    return Object.freeze(ns);
  }

  updateExample(event: any, idx: number): void {
    for (let key in event.state) {
      const nodestate = this.freezeNodeState(event.state[key]);
      this.examples[idx].renderState[key] = nodestate;
    }
  }

  updateTextKeys(apiRequest: any, idx: number) {
    const xml: string | null =
      this.editingForms.indexOf(this.examples[idx].name) === -1
        ? null
        : this.examples[idx].form;
    apiRequest.commands.push({
      type: "view-node-fire-event",
      id: this.examples[idx].renderState.root,
      name: "ui-customiseNamespace",
      data: {
        service: this.examples[idx].name,
        text: this.texts,
        xml
      }
    });
  }

  addText() {
    if (this.texts == null) {
      this.texts = {};
    }
    this.texts["NEW"] = "Enter Text";
    this.$forceUpdate();
  }

  removeText(key: string) {
    if (this.texts != null) {
      delete this.texts[key];
    }
    this.$forceUpdate();
  }

  get textAreaStyle(): any {
    return {
      width: "100%",
      height: "250px"
    };
  }

  gen(event: any, index: number): void {
    const t = this;
    const prefix = document.location.pathname;
    globalThis.jQuery.ajax({
      method: "POST",
      url: prefix + "/fiddle/" + this.name + "/" + this.examples[index].name,
      dataType: "json",
      data: {
        fiddle: event.target.previousElementSibling.value,
        text: this.texts
      },
      success: function (data, textStatus) {
        t.examples[index].form = data.form;
        t.examples[index].renderState = data.renderState;
        t.examples[index].id = data.id;
        t.examples[index].sort = data.sort;
      },
      error: function (xhr) {
        alert("error");
        console.log(xhr);
      }
    });
  }

  markdown(md: string): string {
    if ((window as any).marked) {
      return (window as any).marked(md);
    }
    return md;
  }

  codeBlock(code: string, type: string): string {
    let block: string = "```" + type + "\n" + code + "\n````";
    return this.markdown(block);
  }

  reset() {
    this.examples = [];
    this.editingForms = [];
    this.getDemo();
  }

  getDemo(): any {
    const t = this;

    let processDone, processReject;
    let p = new Promise(function (resolve, reject) {
      processDone = resolve;
      processReject = reject;
    });

    const prefix = document.location.pathname;
    globalThis.jQuery.ajax({
      method: "POST",
      url: prefix + "/demo/" + this.name,
      dataType: "json",
      data: { unresolved: this.unresolved },
      success: function (data, textStatus) {
        if (!data || !data.services) {
          if (processReject) {
            processReject();
          }
          console.error(
            "ajax:post url:demo/ failed response data or data.services is empty. Please check the configured example services for any errors."
          );
          return;
        }
        Object.keys(data.services).forEach((key) => {
          t.examples.push({
            idx: -1,
            name: key,
            form: data.services[key].form,
            renderState: data.services[key].renderState,
            description: data.services[key].description,
            title: data.services[key].title,
            id: data.services[key].id,
            sort: data.services[key].sort
          });
        });
        t.examples.sort((a, b) =>
          a.sort === b.sort ? 0 : a.sort > b.sort ? 1 : -1
        );
        if (!t.unresolved) {
          t.texts = data.text as TextConfig;
          t.ruleReferences = data.ruleReferences as RuleReference[];
          t.dataConstraints = data.dataConstraints as DataConstraintConfig[];
        }

        t.filteredExamples = t.examples;

        t.examples.forEach(function (el, idx) {
          t.filteredExamples[idx].idx = idx;
        });

        if (t.exampleIds && t.exampleIds.length > 0) {
          t.filteredExamples = t.filteredExamples.filter(function (example) {
            return t.exampleIds.some(function (filterId) {
              return filterId === example.name;
            });
          });
        }
        processDone(data);
      },
      error: function (xhr) {
        console.log(xhr);
        if (processReject) {
          processReject();
        }
      }
    });

    return p;
  }

  mounted() {
    const event = new CustomEvent("ui-block", { detail: {} });
    document.dispatchEvent(event);

    let t = this;
    this.getDemo()
      .then(function (data) {})
      .catch(function (err) {
        t.configError = true;
      })
      .finally(function () {
        const event = new CustomEvent("ui-unblock");
        document.dispatchEvent(event);
      });
  }
}
</script>
